/**
 * Client-side hostname shape validation for the wizard's "bind a hostname"
 * step (journey.md rung 4, G8). This is advisory only — the server
 * canonicalizes and is the actual authority (`createDomain`, ADR-0026 §3) —
 * but catching an obviously-invalid hostname here means the operator sees a
 * problem before an extra round trip.
 *
 * Rule: lowercase labels of `[a-z0-9-]`, no leading/trailing hyphen on any
 * label, at least two labels (a bare single word is not a routable
 * hostname), overall length <= 253.
 */
const HOSTNAME_LABEL_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export function isValidHostname(value: string): boolean {
  if (value.length === 0 || value.length > 253) return false;
  const labels = value.split(".");
  if (labels.length < 2) return false;
  return labels.every((label) => HOSTNAME_LABEL_RE.test(label));
}

/**
 * The scheme the wizard shows alongside a bound hostname: `.localhost`
 * names resolve to loopback with no certificate involved (ADR-0034 — a
 * local node's edge binds loopback only and disables ACME), so they are
 * always `http`; every other hostname is routed through the real edge with
 * ACME, so it is always `https`.
 */
export function schemeForHostname(hostname: string): "http" | "https" {
  return hostname.endsWith(".localhost") ? "http" : "https";
}

export function urlForHostname(hostname: string): string {
  return `${schemeForHostname(hostname)}://${hostname}`;
}
